import React from 'react';
import { Navigate, useParams } from 'react-router';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import AddDocumentsPage from './AddDocumentsPage';
import FilesPage from './FilesPage';
import SiestaAIGenerated from './SiestaAIGenerated';

export const FileUploadTabNames = {
  ToReview: 'add',
  ToReviewFromOneDrive: 'add-from-one-drive',
  Reviewed: 'files',
  SuitabilityReports: 'suitability-reports',
  MeetingAgendas: 'meeting-agendas',
  MeetingNotes: 'meeting-notes',
  DataExports: 'data-exports',
  SiestaAIGenerated: 'generated',
} as const;

type FileUploadTabName = (typeof FileUploadTabNames)[keyof typeof FileUploadTabNames];

function isTabName(value?: string): value is FileUploadTabName {
  if (!value) {
    return false;
  }

  return Object.values(FileUploadTabNames).includes(value as FileUploadTabName);
}

export default function FileUploadPage() {
  const { sectionSlug: tab, folderId } = useParams<{ sectionSlug: string; folderId?: string }>();
  const { clientId } = useCurrentClient();

  const isFolderRoute = tab?.startsWith('folder/') || !!folderId;
  const selectedTab = isFolderRoute ? FileUploadTabNames.Reviewed : tab;

  if (!isFolderRoute && !isTabName(selectedTab)) {
    return <Navigate to={`/client/${clientId}/document/${FileUploadTabNames.ToReview}`} replace />;
  }

  switch (selectedTab) {
    case FileUploadTabNames.ToReview:
    case FileUploadTabNames.ToReviewFromOneDrive:
      return <AddDocumentsPage />;
    case FileUploadTabNames.Reviewed:
      return <FilesPage />;
    case FileUploadTabNames.SuitabilityReports:
      return <SiestaAIGenerated documentTypeFilter="suitability-report" />;
    case FileUploadTabNames.MeetingAgendas:
      return <SiestaAIGenerated documentTypeFilter="meeting-agenda" />;
    case FileUploadTabNames.MeetingNotes:
      return <SiestaAIGenerated documentTypeFilter="meeting-note" />;
    case FileUploadTabNames.DataExports:
      return <SiestaAIGenerated documentTypeFilter="data-export" />;
    case FileUploadTabNames.SiestaAIGenerated:
      return <SiestaAIGenerated />;
    default:
      return (
        <Navigate to={`/client/${clientId}/document/${FileUploadTabNames.ToReview}`} replace />
      );
  }
}
